import { InsightTag } from '@ecosystem/newron-client-collections/build/shared/types';
import { getPeriodicityForMATAndYTD } from '@utils/PeriodUtils';
import {
  addPgLoadedTagInPlace,
  getGeneralCollectionInsightTags,
  getIsUsingCollectionLiveData,
  getPeriodLabel,
} from '@utils/collectionUtils/CollectionUtils';
import { RelativePeriod } from '../../comparison-period-selector/Types';
import { TradingAssistantButterflyChartInsight } from './Model';
import { capitalizeFirstLetter } from '@utils/StringUtils';
import { startCase } from 'lodash';
import { BrandDependencyKpi } from '@models/BrandDependency';
import { kpiToOption } from '@data-visualization/brand-dependency-chart/BrandDependencyChartUtils';
import { getRequireAllPgLoadedInCollections } from '@components/collections/CollectionsFlagsByInsightType';
import { CollectionInsightType } from '@components/collections/Model';

export const getButterflyChartInsightInfoTags = async (
  insight: TradingAssistantButterflyChartInsight,
): Promise<InsightTag[]> => {
  const {
    apiParams: { clientId, periodType, period, comparisonPeriod, countries, productType, productGroup, appliedFilters },
    selectedGrowthType,
    kpi,
    sortBy,
    featureOption,
    collectionDataType,
    retailerClientName,
  } = insight;

  const effectivePeriod = period;
  const baseTimePeriod = {
    label: effectivePeriod,
    value: effectivePeriod,
    lastUpdatedDate: 0,
  };
  const { periodicity: effectivePeriodicity } = getPeriodicityForMATAndYTD(baseTimePeriod, periodType);
  const requireAllPgLoadedInCollections = getRequireAllPgLoadedInCollections(
    CollectionInsightType.TradingAssistantButterflyChart,
  );

  const periodLabel = await getPeriodLabel({
    clientId,
    periodicity: effectivePeriodicity,
    baseTimePeriod,
    comparisonPeriod: comparisonPeriod as RelativePeriod,
    countryIds: countries.map(({ id }) => id),
    productType,
    collectionDataType: getIsUsingCollectionLiveData(period, collectionDataType) ? 'live' : collectionDataType,
    productGroups: productGroup,
  });

  const baseTags = getGeneralCollectionInsightTags({
    periodType: effectivePeriodicity,
    comparisonPeriod:
      typeof comparisonPeriod === 'object' && comparisonPeriod !== null
        ? JSON.stringify(comparisonPeriod)
        : String(comparisonPeriod),
    selectedGrowthType: selectedGrowthType,
    appliedFilters: appliedFilters,
    period: periodLabel,
    retailerClientName: retailerClientName,
  });
  const baseApiParams = {
    clientId: clientId,
    productType: productType,
    periodType: periodType,
    period: baseTimePeriod.value,
    comparisonPeriod: comparisonPeriod as RelativePeriod,
    countries: countries,
    productGroup: productGroup,
    appliedFilters: appliedFilters,
  };
  await addPgLoadedTagInPlace(baseTags, baseApiParams, collectionDataType, requireAllPgLoadedInCollections);
  const allKpis = Object.values(BrandDependencyKpi).map((k) => kpiToOption(k));
  const selectedKpi = allKpis.find((o) => o.value === kpi) ?? allKpis[0];
  const otherTags = [
    {
      key: 'Show',
      value: capitalizeFirstLetter(selectedKpi.label),
    },
    ...(featureOption.label !== 'Select'
      ? [
          {
            key: 'By',
            value: capitalizeFirstLetter(featureOption.label),
          },
        ]
      : []),
    {
      key: 'Sorted by',
      value: capitalizeFirstLetter(startCase(sortBy)),
    },
  ];

  return [...baseTags, ...otherTags];
};
